import React, { useState, useEffect, useCallback } from 'react';
import negocioDiretorioService from '../services/negocioDiretorio.service';
import { usePermissoes }       from '../context/PermissoesContext';
import Modal                   from './Modal';

// ════════════════════════════════════════════════════════════════════════════
// DiretoriosModal — lista de diretórios (pastas locais/de rede) do negócio.
//
// Abrir no Windows Explorer: navegador NÃO deixa página web abrir pasta local
// (file:// é bloqueado). Por isso o ícone usa o protocolo `alphadir:`, que é
// registrado na máquina do usuário UMA vez pelo instalador
// /ferramentas/instalar-abrir-diretorio.cmd (sem admin, só HKCU). Sem o
// instalador, o botão "copiar" deixa o caminho pronto pra colar no Explorer.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:     '#003399',
  azulBg:   '#eff6ff',
  borda:    '#e8eaed',
  texto:    '#1a1a2e',
  muted:    '#888',
  branco:   '#fff',
  verde:    '#1D9E75',
  verdeBg:  '#f0fff4',
  vermelho: '#e53935',
  vermBg:   '#fff0f0',
  amarelo:  '#d97706',
  amlBg:    '#fffbeb',
  cinzaBg:  '#fafafa',
};

const URL_INSTALADOR = '/ferramentas/instalar-abrir-diretorio.cmd';

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '14px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: "'Manrope', sans-serif",
};

const svgProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const SvgPastaAbrir = ({ s = 16 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <polyline points="10 14 14 10" /><polyline points="11 10 14 10 14 13" />
  </svg>
);
const SvgCopiar = ({ s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const SvgLapis = ({ s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const SvgLixeira = ({ s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const SvgMais = ({ s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...svgProps} style={{ display: 'block' }}>
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const btnIcone = (cor) => ({
  width: '30px', height: '30px', padding: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  backgroundColor: COR.branco, color: cor, cursor: 'pointer',
  textDecoration: 'none',
});

const btnTexto = (cor, bg) => ({
  padding: '8px 16px', fontSize: '13px', fontWeight: '600',
  border: `1px solid ${cor}`, borderRadius: '5px',
  backgroundColor: bg, color: cor, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  fontFamily: "'Manrope', sans-serif",
});

// alphadir:<caminho codificado> — o handler instalado decodifica e abre no Explorer.
const urlExplorer = (caminho) => `alphadir:${encodeURIComponent(caminho)}`;

function formatarData(v) {
  if (!v) return '';
  const d = new Date(v);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const FORM_VAZIO = { id: null, caminho: '', descricao: '' };

function DiretoriosModal({ aberto, negocio, onFechar }) {
  const { pode } = usePermissoes();
  const podeAlterar = pode('crm', 'alterar');

  const [lista, setLista]         = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [form, setForm]           = useState(FORM_VAZIO);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando]   = useState(false);
  const [erro, setErro]           = useState('');
  const [aviso, setAviso]         = useState('');

  const carregar = useCallback(async () => {
    if (!negocio?.id) return;
    setCarregando(true);
    try {
      setLista(await negocioDiretorioService.listar(negocio.id));
    } catch (e) {
      setErro(e?.response?.data?.mensagem || 'Erro ao carregar diretórios.');
    } finally {
      setCarregando(false);
    }
  }, [negocio?.id]);

  useEffect(() => {
    if (aberto) {
      setErro(''); setAviso('');
      setForm(FORM_VAZIO); setMostrarForm(false);
      carregar();
    }
  }, [aberto, carregar]);

  const abrirNovo = () => { setForm(FORM_VAZIO); setErro(''); setMostrarForm(true); };
  const abrirEdicao = (d) => {
    setForm({ id: d.id, caminho: d.caminho || '', descricao: d.descricao || '' });
    setErro(''); setMostrarForm(true);
  };
  const cancelarForm = () => { setForm(FORM_VAZIO); setMostrarForm(false); setErro(''); };

  const salvar = async () => {
    if (!form.caminho.trim()) { setErro('Informe o caminho do diretório.'); return; }
    setSalvando(true); setErro('');
    try {
      const dados = { caminho: form.caminho, descricao: form.descricao };
      if (form.id) await negocioDiretorioService.alterar(form.id, dados);
      else         await negocioDiretorioService.incluir(negocio.id, dados);
      cancelarForm();
      await carregar();
    } catch (e) {
      setErro(e?.response?.data?.mensagem || 'Erro ao salvar diretório.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (d) => {
    if (!window.confirm(`Excluir o diretório "${d.descricao || d.caminho}" da lista?\n(A pasta em si não é apagada.)`)) return;
    try {
      await negocioDiretorioService.excluir(d.id);
      await carregar();
    } catch (e) {
      setErro(e?.response?.data?.mensagem || 'Erro ao excluir diretório.');
    }
  };

  const copiar = async (caminho) => {
    try {
      await navigator.clipboard.writeText(caminho);
      setAviso('Caminho copiado. Cole na barra de endereço do Windows Explorer (Win+E).');
    } catch {
      setAviso('Não foi possível copiar automaticamente. Selecione o caminho e copie com Ctrl+C.');
    }
    setTimeout(() => setAviso(''), 5000);
  };

  return (
    <Modal
      aberto={aberto}
      titulo={`Diretórios do Negócio ${negocio?.id || ''}`}
      onFechar={onFechar}
      rotina="crm"
      descricao="Pastas (locais ou de rede) relacionadas ao negócio."
      botoesNoTopo
      somenteLeitura
      largura="900px"
    >
      <div style={{ fontFamily: "'Manrope', sans-serif" }}>
        {erro && (
          <div style={{ backgroundColor: COR.vermBg, border: '1px solid #ffcccc', borderRadius: '5px', color: COR.vermelho, fontSize: '13px', padding: '10px 12px', marginBottom: '12px' }}>
            {erro}
          </div>
        )}
        {aviso && (
          <div style={{ backgroundColor: COR.verdeBg, border: '1px solid #a8e6c1', borderRadius: '5px', color: '#1d6a3a', fontSize: '13px', padding: '10px 12px', marginBottom: '12px' }}>
            {aviso}
          </div>
        )}

        {/* Botão novo */}
        {podeAlterar && !mostrarForm && (
          <div style={{ marginBottom: '16px' }}>
            <button type="button" onClick={abrirNovo} style={btnTexto(COR.azul, COR.azulBg)}>
              <SvgMais />Adicionar diretório
            </button>
          </div>
        )}

        {/* Formulário inline */}
        {mostrarForm && (
          <div style={{ border: `1px solid ${COR.borda}`, borderRadius: '6px', padding: '14px', marginBottom: '16px', backgroundColor: COR.cinzaBg }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(260px, 1.6fr)', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: COR.texto, display: 'block', marginBottom: '4px' }}>Descrição</label>
                <input
                  value={form.descricao}
                  onChange={(e) => setForm(f => ({ ...f, descricao: e.target.value }))}
                  placeholder="Ex.: Documentação do projeto"
                  style={inputStyle}
                  autoFocus
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: COR.texto, display: 'block', marginBottom: '4px' }}>
                  Caminho do diretório <span style={{ color: COR.vermelho }}>*</span>
                </label>
                <input
                  value={form.caminho}
                  onChange={(e) => setForm(f => ({ ...f, caminho: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') salvar(); }}
                  placeholder="Ex.: \\servidor\projetos\ABC-0001  ou  C:\Projetos\ABC-0001"
                  style={{ ...inputStyle, fontFamily: 'Consolas, monospace', fontSize: '13px' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button type="button" onClick={cancelarForm} style={btnTexto(COR.muted, COR.branco)} disabled={salvando}>
                Cancelar
              </button>
              <button type="button" onClick={salvar} style={btnTexto(COR.branco, COR.azul)} disabled={salvando}>
                {salvando ? 'Salvando...' : (form.id ? 'Salvar alteração' : 'Adicionar')}
              </button>
            </div>
          </div>
        )}

        {/* Lista */}
        {carregando ? (
          <div style={{ color: COR.muted, fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>Carregando...</div>
        ) : lista.length === 0 ? (
          <div style={{ color: COR.muted, fontSize: '13px', padding: '24px 0', textAlign: 'center', border: `1px dashed ${COR.borda}`, borderRadius: '6px' }}>
            Nenhum diretório cadastrado para este negócio.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COR.borda}`, color: COR.muted, textAlign: 'left' }}>
                <th style={{ padding: '8px 6px', width: '40px' }}></th>
                <th style={{ padding: '8px 6px' }}>Descrição</th>
                <th style={{ padding: '8px 6px' }}>Caminho</th>
                <th style={{ padding: '8px 6px', width: '90px' }}>Incluído</th>
                <th style={{ padding: '8px 6px', width: podeAlterar ? '110px' : '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {lista.map(d => (
                <tr key={d.id} style={{ borderBottom: `1px solid ${COR.borda}` }}>
                  <td style={{ padding: '8px 6px' }}>
                    <a href={urlExplorer(d.caminho)} title="Abrir no Windows Explorer" style={btnIcone(COR.amarelo)}>
                      <SvgPastaAbrir />
                    </a>
                  </td>
                  <td style={{ padding: '8px 6px', color: COR.texto, fontWeight: 600 }}>{d.descricao || '—'}</td>
                  <td style={{ padding: '8px 6px', fontFamily: 'Consolas, monospace', fontSize: '12px', color: COR.texto, wordBreak: 'break-all' }}>
                    {d.caminho}
                  </td>
                  <td style={{ padding: '8px 6px', color: COR.muted, fontSize: '12px' }} title={d.criado_por_nome || ''}>
                    {formatarData(d.criado_em)}
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      <button type="button" onClick={() => copiar(d.caminho)} title="Copiar caminho" style={btnIcone(COR.muted)}>
                        <SvgCopiar />
                      </button>
                      {podeAlterar && (
                        <>
                          <button type="button" onClick={() => abrirEdicao(d)} title="Alterar" style={btnIcone(COR.azul)}>
                            <SvgLapis />
                          </button>
                          <button type="button" onClick={() => excluir(d)} title="Excluir da lista" style={btnIcone(COR.vermelho)}>
                            <SvgLixeira />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Rodapé de ajuda */}
        <div style={{ marginTop: '18px', padding: '10px 12px', backgroundColor: COR.amlBg, border: '1px solid #fde68a', borderRadius: '5px', fontSize: '12px', color: '#7a5a00', lineHeight: 1.5 }}>
          O ícone de pasta abre o diretório no Windows Explorer. Na primeira vez, em cada computador,
          é preciso rodar o instalador:{' '}
          <a href={URL_INSTALADOR} download style={{ color: COR.azul, fontWeight: 600 }}>baixar instalar-abrir-diretorio.cmd</a>
          {' '}(dar duplo clique; não precisa de administrador). Depois, ao clicar no ícone, o navegador pede
          confirmação — marque "Sempre permitir".
        </div>
      </div>
    </Modal>
  );
}

export default DiretoriosModal;
